/** 展示全部持有标签；手动操作只开放预置闭集且由服务端再次校验权限。 */
import type { AdminUserDetailResponse } from '@novel-admin/shared';
import { onlineManager, useQueryClient } from '@tanstack/react-query';
import { AdminNetworkError, AdminRequestAbortedError } from '~/api/errors';
import { useUserPresetTagAssignment, useUserPresetTagDefinitions } from '~/data/queries';
import { toast } from '~/ui/Toast';

export function UserHeldTags({
  userId,
  tags,
  canManage,
  readState,
}: {
  readState?: {
    isError: boolean;
    isFetching: boolean;
    isFetchedAfterMount: boolean;
    refetch: () => void;
  };
  userId: string;
  tags: AdminUserDetailResponse['tags'] | undefined;
  canManage: boolean;
}) {
  const queryClient = useQueryClient();
  const tagsFresh =
    !readState || (!readState.isError && !readState.isFetching && readState.isFetchedAfterMount);
  const definitions = useUserPresetTagDefinitions();
  const assignment = useUserPresetTagAssignment(userId);
  async function changeAssignment(tagId: string, isAssigned: boolean) {
    const currentRead = queryClient.getQueryState(['user-detail', userId]);
    // 事件发送时再核对实时缓存，关闭同一轮刷新尚未触发渲染的旧按钮窗口。
    if (
      !tagsFresh ||
      assignment.isPending ||
      (readState &&
        (!currentRead ||
          currentRead.status !== 'success' ||
          currentRead.fetchStatus !== 'idle' ||
          currentRead.isInvalidated))
    )
      return;
    // 点击时即拒绝离线意图；不能等mutation微任务再检查，否则关闭重连会把旧点击重新授权。
    if (!onlineManager.isOnline()) {
      toast('网络不可用，本次标签操作未发送；恢复后请重新确认并提交。', { tone: 'neg' });
      return;
    }
    try {
      await assignment.mutateAsync({ tagId, isAssigned });
      const detailState = queryClient.getQueryState(['user-detail', userId]);
      const readbackFailed =
        readState &&
        (detailState?.status !== 'success' ||
          detailState.fetchStatus !== 'idle' ||
          detailState.isInvalidated);
      toast(
        readbackFailed
          ? '标签已保存，详情尚未确认，请重新加载标签。'
          : isAssigned
            ? '已打标'
            : '已摘标',
        { tone: readbackFailed ? 'warn' : 'pos' },
      );
    } catch (error) {
      const resultUnknown =
        error instanceof AdminNetworkError ||
        (error instanceof AdminRequestAbortedError && error.serverStateUnknown);
      toast(
        resultUnknown
          ? '标签保存结果未知，请刷新用户详情确认后再操作。'
          : error instanceof Error
            ? error.message
            : '标签保存失败，请重试',
        { tone: 'neg' },
      );
    }
  }
  return (
    <section className="card-flat vstack" style={{ gap: 12 }} aria-label="用户持有标签">
      <strong>用户标签</strong>
      <div className="hstack" style={{ gap: 6, flexWrap: 'wrap' }}>
        {readState?.isError ? (
          <span className="hint">标签读取失败，请重新加载确认当前持有状态。</span>
        ) : !tagsFresh || tags === undefined ? (
          <span className="hint">正在加载标签</span>
        ) : tags.length ? (
          tags.map((tag) => (
            <span
              className="chip"
              key={tag.id}
              title={`${tag.tag_key} · ${tag.binding_status === 'bound' ? '已绑定' : '未绑定'} · ${tag.is_active ? '已启用' : '已禁用'}`}
            >
              {tag.name}
            </span>
          ))
        ) : (
          <span className="hint">暂无持有标签</span>
        )}
      </div>
      {readState?.isError && (
        <button className="btn btn-sm" onClick={() => readState.refetch()}>
          重新加载标签
        </button>
      )}
      {definitions.isError ? (
        <div className="hint">
          预置标签加载失败{' '}
          <button className="btn btn-sm" onClick={() => void definitions.refetch()}>
            重试
          </button>
        </div>
      ) : (
        <div className="vstack" style={{ gap: 6 }}>
          {definitions.data?.map((tag) => {
            const held = tags?.some((item) => item.id === tag.id) ?? false;
            return (
              <div
                className="hstack"
                key={tag.id}
                style={{ justifyContent: 'space-between', gap: 8 }}
              >
                <span>
                  {tag.name} <span className="mono hint">{tag.tag_key}</span>
                </span>
                <button
                  className="btn btn-sm"
                  disabled={!canManage || !tagsFresh || tags === undefined || assignment.isPending}
                  title={canManage ? undefined : '仅超管或管理员可管理预置标签'}
                  onClick={() => void changeAssignment(tag.id, !held)}
                >
                  {held ? '摘标' : '打标'}
                </button>
              </div>
            );
          })}
          {definitions.isLoading && <span className="hint">正在加载预置标签</span>}
        </div>
      )}
    </section>
  );
}
